import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { HelmetProvider } from 'react-helmet-async';
import { PricingProvider } from './utils/PricingContext';

// Import components
import Header from './components/Header';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';
import OnboardingPage from './pages/OnboardingPage';
import RoadmapSetupPage from './pages/RoadmapSetupPage';
import UserProfilePage from './pages/UserProfilePage';
import FreeResultsPage from './pages/FreeResultsPage';
import SubscriptionPage from './pages/SubscriptionPage';
import PremiumDashboardPage from './pages/PremiumDashboardPage';
import CareerInsightsPage from './pages/CareerInsightsPage';
import RoadmapPage from './pages/RoadmapPage';
import RefinementQuestionsPage from './pages/RefinementQuestionsPage';
import DetailedRoadmapPage from './pages/DetailedRoadmapPage';
import B2BAdminDashboardPage from './pages/B2BAdminDashboardPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import ResumeBuilderPage from './pages/ResumeBuilderPage';
import BlogListPage from './pages/BlogListPage';
import BlogDetailPage from './pages/BlogDetailPage';
import CareerExplorerPage from './pages/CareerExplorerPage';
import SkillAssessmentPage from './pages/SkillAssessmentPage';
import QuizResultsPage from './pages/QuizResultsPage';
import VerifyCredentialPage from './pages/VerifyCredentialPage';
import NotFoundPage from './pages/NotFoundPage';
import TaskDeepDivePage from './pages/TaskDeepDivePage';
import TermsPage from './pages/TermsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import RefundPolicyPage from './pages/RefundPolicyPage';
import AboutUsPage from './pages/AboutUsPage';
import ContactUsPage from './pages/ContactUsPage';
import LoadingSpinner from './components/LoadingSpinner';
import OnboardingTutorial from './components/OnboardingTutorial';
import HelpFAQ from './components/HelpFAQ';
import UpgradeBanner from './components/UpgradeBanner';
import UpgradeModal from './components/UpgradeModal';
import NewsletterPopup from './components/NewsletterPopup';
import ExitIntentPopup from './components/ExitIntentPopup';
import ErrorBoundary from './components/ErrorBoundary';
import ScrollToTop from './components/ScrollToTop';

// Import API
import { careerAPI, paymentAPI, featuresAPI, userAPI } from './utils/api';
import authService from './firebase/authService';

// Import styles
import './index.css';

function App() {
  const [appState, setAppState] = useState({
    userProfile: null,
    careerSuggestions: null,
    selectedCareerPath: null,
    roadmapData: null,
    paymentStatus: null,
    isLoading: false
  });
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showHelpFAQ, setShowHelpFAQ] = useState(false);
  const [userTier, setUserTier] = useState('free');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [user, setUser] = useState(null);

  // Load state from localStorage on app start
  useEffect(() => {
    const loadItem = (key) => {
      try {
        const item = localStorage.getItem(key);
        return item ? JSON.parse(item) : null;
      } catch (error) {
        console.warn(`Could not parse ${key} from localStorage:`, error);
        localStorage.removeItem(key); // Clear the corrupted item
        return null;
      }
    };

    setAppState(prevState => ({
      ...prevState,
      userProfile: loadItem('userProfile'),
      careerSuggestions: loadItem('careerSuggestions'),
      roadmapData: loadItem('roadmapData'),
      selectedCareerPath: loadItem('selectedCareerPath'),
      isLoading: false
    }));

    // Check if onboarding should be shown
    const onboardingCompleted = localStorage.getItem('onboardingCompleted');
    if (!onboardingCompleted) {
      setShowOnboarding(true);
    }

    // Capture referral code from URL (?ref=XXXX)
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const refCode = urlParams.get('ref');
      if (refCode) {
        localStorage.setItem('ct_referral_code', refCode);
        // Track the referral visit on the backend
        featuresAPI.trackReferral(refCode).catch(() => { });
        // Clean the URL to remove ?ref= param
        const url = new URL(window.location.href);
        url.searchParams.delete('ref');
        window.history.replaceState({}, '', url.pathname + url.search);
      }
    } catch (e) {
      console.warn('Error capturing referral code:', e);
    }
  }, []);

  // Get user and subscription status
  useEffect(() => {
    const unsubscribe = authService.onAuthStateChange(async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        try {
          const subscription = await paymentAPI.getUserSubscription();
          setUserTier(subscription?.subscription?.tier || 'free');

          // Also fetch global user profile and update app state
          const profileRes = await userAPI.getProfile();
          if (profileRes?.profile) {
            updateAppState({ userProfile: profileRes.profile });
          }
        } catch (error) {
          console.warn('Could not fetch user details:', error);
          setUserTier('free');
        }
      } else {
        setUserTier('free');
      }
    });
    return () => unsubscribe();
  }, []);

  // Show special offer modal after some time for free users
  useEffect(() => {
    if (userTier === 'free' && user) {
      // Check if we've shown the modal recently
      const lastShown = localStorage.getItem('upgrade_modal_shown');
      const hoursSinceShown = lastShown ? (Date.now() - parseInt(lastShown)) / (1000 * 60 * 60) : 999;

      if (hoursSinceShown > 24) { // Show once every 24 hours
        const timer = setTimeout(() => {
          setShowUpgradeModal(true);
          localStorage.setItem('upgrade_modal_shown', Date.now().toString());
        }, 60000); // Show after 1 minute of browsing

        return () => clearTimeout(timer);
      }
    }
  }, [userTier, user]);

  // Update app state
  const updateAppState = (updates) => {
    console.log('Updating app state:', updates);

    // Create new state object
    let newState;
    setAppState(prevState => {
      newState = {
        ...prevState,
        ...updates
      };
      console.log('New app state:', newState);

      // Persist specific items to localStorage
      if (Object.prototype.hasOwnProperty.call(updates, 'userProfile')) {
        localStorage.setItem('userProfile', JSON.stringify(newState.userProfile || null));
      }
      if (Object.prototype.hasOwnProperty.call(updates, 'careerSuggestions')) {
        localStorage.setItem('careerSuggestions', JSON.stringify(newState.careerSuggestions || null));
      }
      if (Object.prototype.hasOwnProperty.call(updates, 'roadmapData')) {
        localStorage.setItem('roadmapData', JSON.stringify(newState.roadmapData || null));
      }
      if (Object.prototype.hasOwnProperty.call(updates, 'selectedCareerPath')) {
        localStorage.setItem('selectedCareerPath', JSON.stringify(newState.selectedCareerPath || null));
      }

      return newState;
    });
  };

  // Clear app state (for new session)
  const clearAppState = () => {
    setAppState({
      userProfile: null,
      careerSuggestions: null,
      selectedCareerPath: null,
      roadmapData: null,
      paymentStatus: null,
      isLoading: false
    });
    localStorage.removeItem('userProfile');
    localStorage.removeItem('careerSuggestions');
    localStorage.removeItem('roadmapData');
    localStorage.removeItem('selectedCareerPath');
  };

  const handleResumeAnalysis = async (file) => {
    updateAppState({ isLoading: true });
    try {
      const suggestions = await careerAPI.analyzeResume(file);

      // The backend returns the profile and suggestions
      const { profile, career_paths } = suggestions;

      // The updateAppState function now handles localStorage persistence
      updateAppState({
        userProfile: profile,
        careerSuggestions: { career_paths },
        isLoading: false,
      });

      // Add a small delay to ensure state is fully updated before navigation
      await new Promise(resolve => setTimeout(resolve, 100));

      return true; // Indicate success
    } catch (error) {
      console.error("Resume analysis failed:", error);
      updateAppState({ isLoading: false });
      return false; // Indicate failure
    }
  };

  return (
    <HelmetProvider>
      <PricingProvider>
        <Router>
          <ErrorBoundary>
            <ScrollToTop />
            <div className="min-h-screen bg-[#08090c] flex flex-col">
              <Header
                hasProfile={!!appState.userProfile}
                onNewSession={clearAppState}
                onShowHelp={() => setShowHelpFAQ(true)}
                onShowOnboarding={() => setShowOnboarding(true)}
              />

              <main className="flex-1 pt-16">
                {appState.isLoading && <LoadingSpinner />}

                <Routes>
                  {/* Home Page */}
                  <Route
                    path="/"
                    element={
                      <HomePage
                        onGetStarted={() => updateAppState({ isLoading: false })}
                        onAnalyzeResume={handleResumeAnalysis}
                      />
                    }
                  />

                  {/* New Global Profile Onboarding */}
                  <Route
                    path="/onboarding"
                    element={<OnboardingPage />}
                  />

                  {/* User Profile Page (Settings/Skills/B2B) */}
                  <Route
                    path="/profile"
                    element={<UserProfilePage />}
                  />

                  {/* Roadmap Context Gatherer */}
                  <Route
                    path="/build-roadmap"
                    element={<RoadmapSetupPage />}
                  />

                  {/* Free Results Page (Conversion funnel) */}
                  <Route
                    path="/free-results"
                    element={<FreeResultsPage />}
                  />

                  {/* Subscription Page */}
                  <Route
                    path="/subscribe"
                    element={<SubscriptionPage />}
                  />

                  {/* Premium Dashboard Page */}
                  <Route
                    path="/premium-dashboard"
                    element={<PremiumDashboardPage />}
                  />

                  {/* Dashboard → Redirect to Premium Dashboard */}
                  <Route
                    path="/dashboard"
                    element={<Navigate to="/premium-dashboard" replace />}
                  />

                  {/* Career Explorer (Public SEO Pages) */}
                  <Route
                    path="/careers"
                    element={<CareerExplorerPage />}
                  />
                  <Route
                    path="/careers/:slug"
                    element={<CareerExplorerPage />}
                  />

                  {/* B2B Admin Area */}
                  <Route
                    path="/b2b-admin"
                    element={<B2BAdminDashboardPage />}
                  />

                  {/* Career Insights Page */}
                  <Route
                    path="/insights"
                    element={
                      <CareerInsightsPage
                        userProfile={appState.userProfile}
                        careerSuggestions={appState.careerSuggestions}
                        onCareerSelect={(careerPath) => {
                          updateAppState({ selectedCareerPath: careerPath });
                        }}
                        onPaymentSuccess={(paymentData) => {
                          updateAppState({
                            paymentStatus: paymentData,
                            isLoading: true
                          });
                        }}
                        onRoadmapGenerated={(roadmapData) => {
                          updateAppState({
                            roadmapData: roadmapData,
                            isLoading: false
                          });
                        }}
                        onCareerSuggestions={(suggestions) => {
                          updateAppState({
                            careerSuggestions: suggestions,
                            isLoading: false
                          });
                        }}
                      />
                    }
                  />

                  {/* Refinement Questions Page */}
                  <Route
                    path="/questions"
                    element={
                      <RefinementQuestionsPage
                        onRoadmapGenerated={(roadmapData) => {
                          updateAppState({
                            roadmapData: roadmapData,
                            isLoading: false
                          });
                        }}
                      />
                    }
                  />

                  {/* Detailed Roadmap Page */}
                  <Route
                    path="/roadmap"
                    element={
                      <DetailedRoadmapPage />
                    }
                  />

                  {/* Task Deep Dive Routing */}
                  <Route
                    path="/roadmap/:roadmapId/week/:weekIndex/task/:taskIndex"
                    element={
                      <TaskDeepDivePage />
                    }
                  />

                  {/* Legacy Roadmap redirect */}
                  <Route
                    path="/roadmap-legacy"
                    element={<Navigate to="/roadmap" replace />}
                  />

                  {/* Payment Success Page */}
                  <Route
                    path="/payment-success"
                    element={
                      <PaymentSuccessPage
                        userProfile={appState.userProfile}
                        selectedCareerPath={appState.selectedCareerPath}
                        onRoadmapGenerated={(roadmapData) => {
                          updateAppState({
                            roadmapData: roadmapData,
                            isLoading: false
                          });
                        }}
                      />
                    }
                  />

                  {/* Resume Builder Page */}
                  <Route
                    path="/resume-builder"
                    element={<ResumeBuilderPage />}
                  />

                  {/* Blog Pages */}
                  <Route
                    path="/blog"
                    element={<BlogListPage />}
                  />
                  <Route
                    path="/blog/:slug"
                    element={<BlogDetailPage />}
                  />

                  {/* Skill Assessment / Credentials */}
                  <Route path="/assessment" element={<SkillAssessmentPage />} />
                  <Route path="/assessment/results" element={<QuizResultsPage />} />
                  <Route path="/verify/:credentialId" element={<VerifyCredentialPage />} />

                  {/* Legal & Compliance Pages */}
                  <Route path="/terms" element={<TermsPage />} />
                  <Route path="/privacy" element={<PrivacyPolicyPage />} />
                  <Route path="/refund-policy" element={<RefundPolicyPage />} />
                  <Route path="/about" element={<AboutUsPage />} />
                  <Route path="/contact" element={<ContactUsPage />} />

                  {/* 404 Page */}
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </main>

              <Footer />

              {/* Newsletter Popup for Guest Users */}
              <NewsletterPopup />

              {/* Exit Intent Popup for Free Users */}
              <ExitIntentPopup user={user} subscription={{ tier: userTier }} />

              {/* Toast notifications - Modern styled */}
              <Toaster
                position="top-center"
                gutter={12}
                containerStyle={{
                  top: 80,
                }}
                toastOptions={{
                  duration: 4000,
                  style: {
                    background: 'linear-gradient(135deg, #1a1f2e 0%, #0F131F 100%)',
                    color: '#fff',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '12px',
                    padding: '16px 20px',
                    fontSize: '14px',
                    fontFamily: 'ui-monospace, monospace',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.05)',
                    backdropFilter: 'blur(10px)',
                  },
                  success: {
                    style: {
                      background: 'linear-gradient(135deg, #064e3b 0%, #0F131F 100%)',
                      border: '1px solid rgba(16, 185, 129, 0.3)',
                    },
                    iconTheme: {
                      primary: '#10b981',
                      secondary: '#fff',
                    },
                  },
                  error: {
                    style: {
                      background: 'linear-gradient(135deg, #7f1d1d 0%, #0F131F 100%)',
                      border: '1px solid rgba(239, 68, 68, 0.3)',
                    },
                    iconTheme: {
                      primary: '#ef4444',
                      secondary: '#fff',
                    },
                  },
                  loading: {
                    style: {
                      background: 'linear-gradient(135deg, #1e3a5f 0%, #0F131F 100%)',
                      border: '1px solid rgba(59, 130, 246, 0.3)',
                    },
                  },
                }}
              />

              {/* Onboarding Tutorial */}
              <OnboardingTutorial
                isOpen={showOnboarding}
                onClose={() => setShowOnboarding(false)}
                onComplete={() => setShowOnboarding(false)}
              />

              {/* Help & FAQ */}
              <HelpFAQ
                isOpen={showHelpFAQ}
                onClose={() => setShowHelpFAQ(false)}
              />

              {/* Upgrade Banner - Shows at bottom for free users */}
              <UpgradeBanner
                userTier={userTier}
                variant="bottom"
              />

              {/* Upgrade Modal - Special offer popup */}
              <UpgradeModal
                isOpen={showUpgradeModal}
                onClose={() => setShowUpgradeModal(false)}
                variant="special-offer"
              />
            </div>
          </ErrorBoundary>
        </Router>
      </PricingProvider>
    </HelmetProvider>
  );
}

export default App;